/**
 * Shared "Verified" trust badge for any data-backed section of a spec page.
 * Shows the source name (linked when a URL is available) and the verified
 * date, so every table — vehicles, vehicle_dimensions, safety_ratings, or
 * vehicle_specs — can attribute exactly where its row came from.
 */
export interface VerifiedBadgeProps {
  source?: string | null | undefined;
  sourceUrl?: string | null | undefined;
  verifiedAt?: string | null | undefined;
  className?: string | undefined;
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

/**
 * Ingested rows store the raw API endpoint they came from (vPIC, NHTSA
 * SafetyRatings, fueleconomy.gov web service). Those endpoints answer machines,
 * not browsers — crawlers see them as broken external links and visitors get a
 * JSON blob. Map each one to the official human-readable page for the same
 * dataset, and drop anything that isn't a public HTTPS page.
 */
export function publicSourceUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:") return null;
  const host = parsed.hostname.replace(/^www\./, "");
  const path = parsed.pathname.toLowerCase();

  if (host === "vpic.nhtsa.dot.gov") return "https://vpic.nhtsa.dot.gov/mid/";
  // api.nhtsa.gov record URLs resolve for both people and crawlers, while
  // www.nhtsa.gov answers 403 to every bot — so keep the API record link here.
  if (host === "api.nhtsa.gov") return url;
  if (host === "nhtsa.gov") return null;
  if (host === "fueleconomy.gov") {
    return path.startsWith("/ws/") ? "https://www.fueleconomy.gov/feg/findacar.shtml" : url;
  }
  if (host.endsWith(".gov") || host.endsWith(".org") || host.endsWith(".com")) {
    // Any other stored citation: only link it when it looks like a page, not an API call.
    return path.includes("/api/") || parsed.search ? null : url;
  }
  return null;
}

export function VerifiedBadge({ source, sourceUrl, verifiedAt, className }: VerifiedBadgeProps) {
  const date = formatDate(verifiedAt);
  const href = publicSourceUrl(sourceUrl);
  const title = [
    source ? `Source: ${source}` : null,
    date ? `Verified ${date}` : null,
  ]
    .filter(Boolean)
    .join(" — ");

  return (
    <span className={`inline-flex flex-col items-end gap-1 ${className ?? ""}`}>
      <span className="verified-badge whitespace-nowrap" title={title || undefined}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Verified
      </span>
      {(source || date) && (
        <span className="text-xs text-muted-foreground text-right">
          {source ? (
            href ? (
              <a
                href={href}
                target="_blank"
                // Source citations point at official government datasets
                // (NHTSA vPIC, api.nhtsa.gov, fueleconomy.gov). These are
                // editorial citations, not untrusted/paid links, so they stay
                // followed for E-E-A-T.
                rel="noopener noreferrer"
                className="hover:text-foreground hover:underline"
              >
                {source}
              </a>
            ) : (
              source
            )
          ) : null}
          {source && date ? " · " : ""}
          {date ? `Verified ${date}` : ""}
        </span>
      )}
    </span>
  );
}
